'use client';

import clsx from 'clsx';
import type { ReactElement } from 'react';
import { X } from 'react-bootstrap-icons';

import { DataTestIds } from '@repo/frontend-shared';
import { SDSIconButton } from '@repo/sds/SDSIconButton';
import { SDSPrimitiveButton } from '@repo/sds/SDSPrimitiveButton';
import { HamburgerIcon } from '@repo/ui/assets/icons/HamburgerIcon';
import { Scroller } from '@repo/ui/components/elements/Scroller/Scroller';
import { HeaderLinkButton } from '@repo/ui/fixtures/header/components/HeaderLinkButton/HeaderLinkButton';
import type { LinkButton } from '@repo/ui/fixtures/header/types/menu-items';

import './HeaderMobileMenu.scss';

import { HEADER_MOBILE_NAV_DROPDOWN_ID, useCoordinatedDropdown } from '@repo/ui/hooks/use-coordinated-dropdown';
import { useLocation } from '@repo/ui/hooks/use-navigation';

interface HeaderMobileMenuProps {
  navLinks: LinkButton[];
  postNavLinkContent?: ReactElement;
}

export function HeaderMobileMenu({ navLinks, postNavLinkContent }: HeaderMobileMenuProps) {
  const { pathname } = useLocation();
  const {
    isOpen: menuOpen,
    close: closeMenu,
    toggle: toggleMenu,
  } = useCoordinatedDropdown(HEADER_MOBILE_NAV_DROPDOWN_ID);

  return (
    <div className="HeaderMobileMenu">
      <SDSPrimitiveButton
        dataTestId={DataTestIds.MOBILE.MENU_OPEN_BTN}
        className={clsx('HeaderMobileMenu__hamburger', { 'HeaderMobileMenu__hamburger--open': menuOpen })}
        onClick={toggleMenu}
        ariaLabel={menuOpen ? 'Close navigation menu' : 'Open navigation menu'}
        ariaPressed={menuOpen}
      >
        <HamburgerIcon />
      </SDSPrimitiveButton>
      <div className={clsx('HeaderMobileMenu__sidebar', { 'HeaderMobileMenu__sidebar--open': menuOpen })}>
        <div className="HeaderMobileMenu__header-row">
          <SDSIconButton
            icon={<X />}
            className="HeaderMobileMenu__close"
            onClick={closeMenu}
            dataTestId={DataTestIds.MOBILE.MENU_CLOSE_BTN}
            ariaLabel="Close menu"
          />
        </div>
        <div className="HeaderMobileMenu__top-section">
          <Scroller>
            <nav className="HeaderMobileMenu__sidebar-nav">
              {navLinks.map(link => (
                <HeaderLinkButton
                  key={link.text}
                  text={link.text}
                  href={link.href}
                  icon={link.icon}
                  subMenuItems={link.subMenuItems}
                  variant="mobile"
                  className={link.href && pathname.startsWith(link.href) ? 'HeaderLinkButton__selected' : ''}
                  textType={link.textType}
                  dataTestId={link.dataTestId}
                />
              ))}
            </nav>
          </Scroller>
        </div>
        {postNavLinkContent ? <div className="HeaderMobileMenu__bottom-section">{postNavLinkContent}</div> : null}
      </div>
    </div>
  );
}
